import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { MAX_LIST_TEXT_LENGTH, MIN_LIST_TEXT_LENGTH } from "@shared/upload-constants";
import { setPendingListText } from "@/lib/pending-list-text";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface TextToOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Prefills the textarea when the dialog opens — used when editing a retry. */
  initialText?: string;
  /**
   * Handle the submitted text instead of the default hand-off. Provided by
   * callers already on /list, which can run extraction directly rather than
   * stashing the text and navigating to themselves.
   */
  onSubmit?: (text: string) => void;
}

/** Non-whitespace character count — line breaks between items shouldn't count toward the minimum. */
const nonWhitespaceLength = (value: string) => value.replace(/\s/g, "").length;

export function TextToOrderDialog({
  open,
  onOpenChange,
  initialText,
  onSubmit,
}: TextToOrderDialogProps) {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const [text, setText] = useState(initialText ?? "");
  const meaningfulLength = nonWhitespaceLength(text);
  const isTooShort = meaningfulLength > 0 && meaningfulLength < MIN_LIST_TEXT_LENGTH;

  // Start each opening from the caller's text, so a retry shows what was
  // submitted before and a fresh open starts empty.
  useEffect(() => {
    if (open) setText(initialText ?? "");
  }, [open, initialText]);

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed || nonWhitespaceLength(trimmed) < MIN_LIST_TEXT_LENGTH) return;
    onOpenChange(false);
    if (onSubmit) {
      onSubmit(trimmed);
      return;
    }
    // Default: hand the text to /list the same way PhotoToOrderPanel hands off
    // a file; the list page consumes it on mount and runs extraction.
    setPendingListText(trimmed);
    setLocation("/list");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-[600px] w-full p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>{t("components.orders.textToOrderDialog.enterYourList")}</DialogTitle>
          <DialogDescription>
            {t("components.orders.textToOrderDialog.typeOrPasteYourList")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Textarea
            id="list-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("components.orders.textToOrderDialog.listPlaceholder")}
            maxLength={MAX_LIST_TEXT_LENGTH}
            autoFocus
            className="min-h-[160px] sm:min-h-[200px] resize-none font-mono text-sm"
            aria-label={t("components.orders.textToOrderDialog.enterYourList")}
          />
          <div className="flex items-center justify-between gap-2 text-xs">
            {isTooShort ? (
              <span className="text-destructive">
                {t("components.orders.textToOrderDialog.listTooShort", {
                  min: MIN_LIST_TEXT_LENGTH,
                })}
              </span>
            ) : (
              <span />
            )}
            <span className="text-muted-foreground text-right">
              {t("components.orders.textToOrderDialog.characterCount", {
                current: text.length,
                max: MAX_LIST_TEXT_LENGTH,
              })}
            </span>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={meaningfulLength < MIN_LIST_TEXT_LENGTH}
            className="w-full sm:w-auto"
          >
            {t("components.orders.textToOrderDialog.createList")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
